import { z } from 'zod';

const NAME_MAX = 255;

const percentagePattern = /^\d+(\.\d{1,4})?$/u;
const amountPattern = /^\d+(\.\d{1,4})?$/u;

const baseTaxSchema = z.object({
  name: z.string().trim().min(1, 'A tax needs a name').max(NAME_MAX),
  type: z.string().min(1, 'Choose a tax type'),
  basis: z.string().min(1, 'Choose how the tax is calculated'),
  rate: z.string(),
  amount: z.string(),
  inclusive: z.boolean(),
  priority: z.string().regex(/^\d+$/, 'Priority must be a whole number 0 or higher'),
  compounds: z.boolean(),
  appliesToFees: z.boolean(),
  enabled: z.boolean(),
});

export const taxFormInput = baseTaxSchema
  .extend({
    propertyId: z.uuid({ message: 'Choose a property' }),
  })
  .superRefine((value, ctx) => {
    if (value.basis === 'PERCENTAGE') {
      if (value.rate === '') {
        ctx.addIssue({
          code: 'custom',
          message: 'A percentage tax needs a rate, e.g. 10.5',
          path: ['rate'],
        });
      } else if (!percentagePattern.test(value.rate)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Must be a percentage, e.g. 10.5',
          path: ['rate'],
        });
      } else if (value.amount !== '') {
        ctx.addIssue({
          code: 'custom',
          message: 'A percentage tax uses a rate, not a flat amount',
          path: ['amount'],
        });
      }
    } else {
      if (value.amount === '') {
        ctx.addIssue({
          code: 'custom',
          message: `A ${value.basis.toLowerCase().replace(/_/gu, ' ')} tax needs an amount`,
          path: ['amount'],
        });
      } else if (!amountPattern.test(value.amount)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Must be a decimal amount, e.g. 12.50',
          path: ['amount'],
        });
      } else if (value.rate !== '') {
        ctx.addIssue({
          code: 'custom',
          message: 'A flat tax uses an amount, not a rate',
          path: ['rate'],
        });
      }
    }
  });

export type TaxFormInput = z.infer<typeof taxFormInput>;

const taxDetail = z.object({
  id: z.string(),
  propertyId: z.string(),
  type: z.string(),
  name: z.string(),
  basis: z.string(),
  rate: z.string().nullable(),
  amount: z.string().nullable(),
  currency: z.string(),
  inclusive: z.boolean(),
  priority: z.number(),
  compounds: z.boolean(),
  appliesToFees: z.boolean(),
  enabled: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number(),
});

export const taxDetailSchema = taxDetail;
export type TaxDetail = z.infer<typeof taxDetailSchema>;

export const taxRow = taxDetail.partial().extend({
  id: z.string(),
});
export type TaxRow = z.infer<typeof taxRow>;
